import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import type { Plan, ProductPlatform, TransitionToPaidRequestDto } from "client/api-v2";
import DatePickerInput from "components/DatePickerInput/DatePickerInput";
import { billableTiers } from "../utils/plan-utils";
import { getTermDateNotices } from "../utils/term-date-utils";
import { PlanSelector } from "./shared/PlanSelector";
import { ZUORA_KEY_REGEX, ZUORA_KEY_VALIDATION_MESSAGE } from "./zuoraKeyValidation";

// The pickers are labelled UTC and hand back UTC-anchored dates, so every term comparison stays in UTC.
const utcToday = () => DateTime.utc().startOf("day");
const defaultTermEnd = () => utcToday().plus({ years: 1 });

interface TransitionToPaidDialogProps {
  open: boolean;
  subscriptionName: string;
  currentLifecycleType: string;
  platform: ProductPlatform;
  handleClose: () => void;
  submitTransition: (request: TransitionToPaidRequestDto) => Promise<void>;
}

export function TransitionToPaidDialog({
  open,
  subscriptionName,
  currentLifecycleType,
  platform,
  handleClose,
  submitTransition,
}: TransitionToPaidDialogProps) {
  const today = utcToday();

  const [zuoraSubscriptionKey, setZuoraSubscriptionKey] = useState<string>("");
  const [termStartDate, setTermStartDate] = useState<DateTime>(utcToday());
  const [termEndDate, setTermEndDate] = useState<DateTime>(defaultTermEnd());
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // PlanSelector is the shared plan picker from the create-subscription form, so the same plan list and
  // eligibility rules apply in both places. It is React Hook Form-based, hence this form purely to give
  // it a `control` — the remaining fields stay uncontrolled state, matching the sibling transition dialogs.
  const { control, watch, reset } = useForm<{ plan?: Plan }>({ defaultValues: { plan: undefined } });
  const selectedPlan = watch("plan");

  // Nothing is preselected: the subscription's existing plan may itself be superseded or expired, and a
  // paid deal should never inherit a dead plan by default. PlanSelector already excludes ineligible plans.
  useEffect(() => {
    if (open) {
      reset({ plan: undefined });
      setZuoraSubscriptionKey("");
      setTermStartDate(utcToday());
      setTermEndDate(defaultTermEnd());
      setReason("");
      setError(undefined);
    }
  }, [open, reset]);

  const keyIsWellFormed = ZUORA_KEY_REGEX.test(zuoraSubscriptionKey.trim());

  // The plan itself says so: the backend clears rather than carrying the overrides across, because an
  // override on such a plan can only reduce what the customer was moved onto.
  const planClearsOverrides = selectedPlan?.clearsEntitlementOverrides === true;

  const { blockingError: termDateError, advisoryWarning: termDateWarning } = getTermDateNotices(
    termStartDate,
    termEndDate,
    today
  );

  const isValid = useMemo(() => {
    if (!selectedPlan?.id) return false;
    if (!keyIsWellFormed) return false;
    if (!reason.trim() || reason.length > 500) return false;
    if (!termStartDate || !termEndDate) return false;
    if (termDateError) return false;
    return true;
  }, [selectedPlan, keyIsWellFormed, reason, termStartDate, termEndDate, termDateError]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(undefined);
    try {
      const request: TransitionToPaidRequestDto = {
        planId: selectedPlan?.id ?? "",
        zuoraSubscriptionKey: zuoraSubscriptionKey.trim(),
        termStartDate: termStartDate.toISODate() ?? "",
        termEndDate: termEndDate.toISODate() ?? "",
        reason: reason.trim(),
      };
      await submitTransition(request);
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Transition to Paid</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Lifecycle:</strong> {currentLifecycleType}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <Alert severity="info">
              This records a subscription that is already billed in Zuora — it does not create or amend this
              subscription in Zuora. Create the Zuora subscription first, then enter its key here.{" "}
              {planClearsOverrides
                ? `${
                    selectedPlan?.name ?? "This plan"
                  } grants the maximum of every limit, so existing entitlement overrides are cleared rather than carried across.`
                : "Existing entitlement overrides are carried across and will be replaced the next time Zuora fulfills this subscription."}
            </Alert>
          </Grid>

          {termDateError && (
            <Grid size={12}>
              <Alert severity="error">{termDateError}</Alert>
            </Grid>
          )}

          {termDateWarning && (
            <Grid size={12}>
              <Alert severity="warning">{termDateWarning}</Alert>
            </Grid>
          )}

          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid size={12}>
            <PlanSelector
              name="plan"
              label="Plan"
              required
              control={control}
              platform={platform}
              allowedTiers={billableTiers}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              required
              fullWidth
              label="Zuora Subscription Key"
              placeholder="SUB12345678"
              value={zuoraSubscriptionKey}
              onChange={(e) => setZuoraSubscriptionKey(e.target.value)}
              error={!!zuoraSubscriptionKey && !keyIsWellFormed}
              helperText={
                !!zuoraSubscriptionKey && !keyIsWellFormed
                  ? ZUORA_KEY_VALIDATION_MESSAGE
                  : "Only one active subscription may hold a given key"
              }
              disabled={isSubmitting}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerInput
              label="Term Start Date"
              value={termStartDate}
              onChange={(date) => setTermStartDate(date ?? today)}
              // Off for the same reason as ReactivateSubscriptionDialog: these are UTC-midnight dates, and
              // the local rendering shows the previous calendar day to anyone west of UTC.
              showLocalConversion={false}
              required
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerInput
              label="Term End Date"
              value={termEndDate}
              onChange={(date) => setTermEndDate(date ?? defaultTermEnd())}
              showLocalConversion={false}
              minDate={DateTime.max(today, termStartDate.plus({ days: 1 }))}
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              rows={4}
              label="Reason"
              placeholder="Why is this subscription being moved to paid?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error && !reason.trim()}
              helperText={
                reason.length > 500
                  ? `Reason is too long (${reason.length}/500 characters)`
                  : !reason.trim() && !!error
                  ? "Reason is required"
                  : ""
              }
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Transitioning..." : "Transition to Paid"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
